<template>
  <div
    class="hardware-mini"
    role="dialog"
    aria-modal="false">
    <div class="section">
      <div class="section-title">CPU</div>
      <div class="row">
        <div class="label">Usage</div>
        <div class="value">{{ cpuDisplay }}</div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">RAM</div>
      <div class="row">
        <div class="label">Usage</div>
        <div class="value">{{ ramDisplay }}</div>
      </div>
      <div
        class="row"
        v-if="ramAvailable !== null">
        <div class="label">Available (RAM)</div>
        <div
          class="value"
          :aria-label="`Available RAM ${ramAvailable}`">
          {{ ramAvailable }}
        </div>
      </div>
    </div>

    <div
      class="section"
      v-if="hasGpu">
      <div class="section-title">GPU</div>
      <div class="row">
        <div class="label">Utilization</div>
        <div class="value">{{ gpuUtilDisplay }}</div>
      </div>
      <div
        class="row"
        v-if="gpuMemDisplay">
        <div class="label">Memory</div>
        <div class="value">{{ gpuMemDisplay }}</div>
      </div>
      <div
        class="row"
        v-if="gpuTempDisplay">
        <div class="label">Temperature</div>
        <div class="value">{{ gpuTempDisplay }}</div>
      </div>
    </div>

    <div
      class="section"
      v-if="hasNetwork">
      <div class="section-title">Network</div>
      <div class="row">
        <div class="label">Overall</div>
        <div class="value">{{ networkUsage }}</div>
      </div>
      <div
        class="row"
        v-for="(iface, idx) in networkIfaces"
        :key="idx">
        <div class="label">{{ iface.name }}</div>
        <div class="value">
          in: {{ iface.in }} / out: {{ iface.out }} ({{ iface.perc_in }})
        </div>
      </div>
    </div>

    <div
      class="section"
      v-if="hasGfxEngine">
      <div class="section-title">Graphics Engine</div>
      <div class="row">
        <div class="label">{{ gfxEngineLabel }}</div>
        <div class="value">{{ gfxEngineStatus }}</div>
      </div>
      <div
        class="row"
        v-if="gfxEngineRunning">
        <div class="label">Handles</div>
        <div
          class="value"
          :class="gfxEngineHandleClass">
          {{ gfxEngineHandlesDisplay }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { deluge } from '@/stores/deluge.js';

function parsePercent(val) {
  if (val == null) return NaN;
  if (typeof val === 'number') return val;
  const s = String(val);
  const m = s.match(/-?\d+(?:\.\d+)?/);
  if (!m) return NaN;
  return parseFloat(m[0]);
}

function formatMemoryGuess(v, total) {
  // v may be a number or numeric string. Return a friendly string with units.
  if (v == null) return null;
  const parsed = typeof v === 'number' ? v : parseFloat(String(v));
  if (!Number.isFinite(parsed)) return String(v);

  // If a total is provided, use its magnitude to infer units
  const t =
    total == null
      ? null
      : typeof total === 'number'
        ? total
        : parseFloat(String(total));

  // Heuristic based on magnitude:
  // - if value < 1000 => likely GiB already
  // - if 1000 <= value < 100000 => likely MiB
  // - if >= 100000 => likely bytes
  if (parsed < 1000) {
    return `${Math.round(parsed * 10) / 10} GiB`;
  }

  if (parsed >= 1000 && parsed < 100000) {
    // interpret as MiB, also show converted GiB
    const gib = parsed / 1024;
    return `${Math.round(gib * 10) / 10} GiB (${Math.round(parsed)} MiB)`;
  }

  // large numbers -> treat as bytes
  const gib = parsed / (1024 * 1024 * 1024);
  return `${Math.round(gib * 10) / 10} GiB`;
}

const cpuDisplay = computed(() => {
  const v = parsePercent(deluge?.hw?.cpu?.usage);
  return Number.isFinite(v) ? `${Math.round(v)} %` : '—';
});

const ramDisplay = computed(() => {
  const v = parsePercent(
    deluge?.hw?.mem?.virtual_memory?.percent ?? deluge?.hw?.mem?.percent
  );
  return Number.isFinite(v) ? `${Math.round(v)} %` : '—';
});

const ramAvailable = computed(() => {
  const v =
    deluge?.hw?.mem?.virtual_memory?.available ?? deluge?.hw?.mem?.available;
  const total =
    deluge?.hw?.mem?.virtual_memory?.total ?? deluge?.hw?.mem?.total;
  if (v == null) return null;
  return formatMemoryGuess(v, total);
});

// GPU can have different key names; pick first sensible numeric value
const gpuDisplay = computed(() => {
  const gpu = deluge?.hw?.gpu;
  if (!gpu) return '—';
  // only consider percentage-like keys to avoid absolute memory numbers
  const keys = [
    'Utilization GPU (%)',
    'mem_usage',
    'temp_usage',
    'usage',
    'utilization',
    'gpu_util',
    'utilization_gpu',
  ];
  for (const k of keys) {
    const val = parsePercent(gpu[k]);
    if (Number.isFinite(val) && val >= 0 && val <= 100)
      return `${Math.round(val)} %`;
  }
  return '—';
});

const hasGpu = computed(() => !!deluge?.hw?.gpu);

const gpuUtilDisplay = computed(() => {
  const gpu = deluge?.hw?.gpu;
  if (!gpu) return '—';
  const keys = [
    'Utilization GPU (%)',
    'usage',
    'utilization',
    'gpu_util',
    'utilization_gpu',
  ];
  for (const k of keys) {
    const v = parsePercent(gpu[k]);
    if (Number.isFinite(v) && v >= 0 && v <= 100) return `${Math.round(v)} %`;
  }
  return '—';
});

const gpuMemDisplay = computed(() => {
  const gpu = deluge?.hw?.gpu;
  if (!gpu) return null;
  const total =
    gpu['Memory Total (Mib)'] ??
    gpu['Memory Total (MiB)'] ??
    gpu['memory_total'];
  const used =
    gpu['Memory Used (Mib)'] ?? gpu['Memory Used (MiB)'] ?? gpu['memory_used'];
  if (total != null && used != null) return `${used} / ${total} MiB`;
  if (gpu.mem_usage != null)
    return `${Math.round(parsePercent(gpu.mem_usage))} %`;
  return null;
});

const gpuTempDisplay = computed(() => {
  const gpu = deluge?.hw?.gpu;
  if (!gpu) return null;
  const temp =
    gpu['Temperature GPU'] ?? gpu['temp_usage'] ?? gpu['temperature'];
  if (temp == null) return null;
  const v = parsePercent(temp);
  if (!Number.isFinite(v)) return null;
  return `${Math.round(v)} °C`;
});

const hasNetwork = computed(
  () => !!deluge?.hw?.network && Object.keys(deluge.hw.network).length > 0
);

const networkUsage = computed(() => {
  const v = parsePercent(deluge?.hw?.network?.usage);
  return Number.isFinite(v) ? `${Math.round(v)} %` : '—';
});

const networkIfaces = computed(() => {
  const net = deluge?.hw?.network;
  if (!net) return [];
  const out = [];
  for (const k of Object.keys(net)) {
    if (k === 'usage') continue;
    const iface = net[k];
    if (!iface) continue;
    const inVal = iface.in ?? iface.rx ?? iface.received;
    const outVal = iface.out ?? iface.tx ?? iface.sent;
    const perc = iface.perc_in ?? iface.perc ?? '';
    out.push({
      name: k,
      in: inVal ?? '—',
      out: outVal ?? '—',
      perc_in: perc ?? '—',
    });
  }
  return out;
});

// Graphics engine handle monitoring (Ventuz or Orb depending on license)
const hasGfxEngine = computed(() => deluge?.hw?.gfx_engine != null);

const gfxEngineRunning = computed(
  () => deluge?.hw?.gfx_engine?.running === true
);

const gfxEngineLabel = computed(() => {
  const engine = deluge?.hw?.gfx_engine?.engine;
  return engine || 'Engine';
});

const gfxEngineStatus = computed(() => {
  const v = deluge?.hw?.gfx_engine;
  if (!v) return '—';
  return v.running ? 'Running' : 'Not Running';
});

const gfxEngineHandlesDisplay = computed(() => {
  const handles = deluge?.hw?.gfx_engine?.handles;
  if (handles == null) return '—';
  // Format with thousands separator
  return handles.toLocaleString();
});

const gfxEngineHandleClass = computed(() => {
  const handles = deluge?.hw?.gfx_engine?.handles;
  if (handles == null) return '';
  // Color code based on handle count thresholds
  if (handles >= 500_000) return 'handle-critical';
  if (handles >= 250_000) return 'handle-warning';
  if (handles >= 100_000) return 'handle-caution';
  return '';
});
</script>

<style scoped>
.hardware-mini {
  width: 230px;
  max-width: 230px;
  min-width: 230px;
  padding: 12px;
  /* Always use dark theme for this popup for consistency */
  background: #1a1a1f;
  color: white;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
}
.row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
}
.label {
  opacity: 0.9;
}
.value {
  font-weight: bold;
}

/* subtle separators */
.row + .row {
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  margin-top: 6px;
  padding-top: 8px;
}

/* stronger visual divider between sections */
.section {
  padding-bottom: 6px;
}
.section:not(:last-child) {
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.section-title {
  font-weight: 600;
  margin-bottom: 6px;
  font-size: 13px;
  opacity: 0.95;
}

/* Ventuz handle count warning colors */
.handle-caution {
  color: #f0ad4e; /* Yellow/amber for 100K+ */
}
.handle-warning {
  color: #ff8c00; /* Orange for 250K+ */
}
.handle-critical {
  color: #dc3545; /* Red for 500K+ */
  animation: pulse-warning 1s ease-in-out infinite;
}

@keyframes pulse-warning {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}
</style>
